import * as Globals from "../../globals";
import {HBEWallDefaultThickness} from "../../globals";
import {findChildrenByName, setBox3FromObjectArray} from "../../utils/threeUtils";
import {buildingGroup, isFreeWallMode, isOrthogonalMode, mousePosition, tmpGroup} from "../../main";
import {Box3, Euler, Mesh, PlaneGeometry, Raycaster, Vector3} from "three";
import {degrees90} from "../../constants/mathConstants";
import {previewMaterial, previewNotAllowedMaterial} from "../../constants/materialConstants";
import {setObjectLayer} from "../../utils/layerUtils";
import {nearestAxisCoordinate, parallelCenterline, snapEndToFace, snapPointToGridCorners, SNAP_ALIGN_LATERAL, SNAP_AXIS_TOLERANCE, SNAP_CLOSE_TOLERANCE, SNAP_JOIN_ALONG, SNAP_MIN_HIT, SNAP_MIN_SEGMENT} from "../../utils/snappingUtils";
import {chooseStartCorner, landsOnMember, pickChainEnd} from "../../utils/chainSnapping";
import {isOuterShellClosed} from "../../utils/wallAnalyzer";
import {directionAxisName, directionSign, wallDirection, wallDirectionAxis, wallSnapAxis} from "../walls/wallGeneration";
import {toFixedFloat} from "../../utils/utils";
import {TransactionManager} from "../../TransactionManager";
import {Text} from "../baseElements/text";
import {SupportBeam} from "./supportBeam";
import {clearBeamPreviewState} from "./supportBeamGeneration";
import {controlObjectName, pillarMeshName, raehmMeshName, supportBeamMeshName} from "../../constants/nameConstants";
import {addVisualAxis, removeVisualAxis} from "../../utils/visualAxisUtils";
import {render} from "../../sceneManager";

const gridSnapTolerance = 0.045;
const wallSnapTolerance = 0.05;
const closeWallSnapTolerance = 0.075;
const wallRaycastTolerance = 0.001;
let beamVectorOffset = 0.5 * Globals.HBEWallDefaultThickness;
const previewYPosition = 4;
const beamCornerStyles = ["inner"];

let raehmRaycaster = new Raycaster();
raehmRaycaster.params.Mesh.threshold = 0.1;
raehmRaycaster.layers.enable(Globals.layers.raehm);
raehmRaycaster.layers.enable(Globals.layers.supportBeam);
raehmRaycaster.layers.enable(Globals.layers.pillars);

export class SupportBeamPreview {
    /**
     * Klasse für die Erstellung einer Unterzugvorschau. Diese benötigt die Dicke des Unterzugs aus dem jeweiligen Dialog.
     * @param thickness - Dicke des zu erstellenden Unterzugs. Standard ist eine Wanddicke. Kann per Dialog oder später per verknüpfter Logik aber davon abweichen.
     * @param forceEvenPillarAmount
     */
    constructor(thickness = HBEWallDefaultThickness, forceEvenPillarAmount = false) {
        this.thickness = thickness;
        beamVectorOffset = 0.5 * this.thickness;
        this.allRaehmMeshes = findChildrenByName(buildingGroup, raehmMeshName, false, true);
        this.pillarMeshes = findChildrenByName(buildingGroup, pillarMeshName, false, true);
        this.beamMeshes = findChildrenByName(buildingGroup, supportBeamMeshName, false, true);
        this.controlObjects = findChildrenByName(buildingGroup, controlObjectName, false, true);

        this.firstPreviewMesh = null; //Erstes Vorschau Viereck. Wird beim Klick auf Wandbutton erstellt
        this.secondPreviewMesh = null; //Zweite Vorschau der zu erstellenden Wand. Wird nach Platzierung des ersten Punkts/Vorschau generiert
        this.firstBeamPlaced = false; //Wird getogglet um festzuhalten ob während der aktuellen Vorschau schon eine Wand platziert wurde
        this.previousDirection = null; //Richtung der zuletzt gesetzt Wand während aktueller Vorschau sofern vorhanden
        this.previousEndPoint = null;
        this.chain = null; //First beam of this run {start, axis, sign}. Used to close the loop.
        this.newBeamPlaced = false;
        this.forceEvenPillarAmount = forceEvenPillarAmount;
        this.validFirstPosition = false;
        this.validSecondPosition = false;
        this.shellWasClosed = isOuterShellClosed();

        this.createFirstPreview();
    }

    //First preview step
    createFirstPreview() {
        this.firstPreviewMesh = this.createFirstPreviewMesh();
        this.adjustFirstPreview();
        tmpGroup.addWithUuid(this.firstPreviewMesh);
    }

    createFirstPreviewMesh() {
        //Generiere Vorschau für ersten Wandklick
        let geometry = new PlaneGeometry(this.thickness, this.thickness);
        geometry.rotateX(degrees90); //Rotieren um auf x/z zu liegen
        let previewStartMesh = new Mesh(geometry, previewMaterial);
        previewStartMesh.name = "beamPreviewStart";
        setObjectLayer(previewStartMesh, Globals.layers.layoutViewOnly);
        return previewStartMesh;
    }

    adjustFirstPreview() {
        let snapPoint = this.snapFirstPreview();
        if (snapPoint === null) {
            snapPoint = mousePosition;
        }
        this.firstPreviewMesh.position.copy(snapPoint);
        this.firstPreviewMesh.position.y = previewYPosition;

        this.checkValidFirstPosition();
    }

    snapFirstPreview() {
        let axisPosition = this.snapPointToAxis(mousePosition);
        if (axisPosition !== null) {
            return axisPosition;
        }

        //Snap an Grid probieren, sofern keine Wand gefunden oder die Toleranz zur Wand überschritten wurde.
        let gridPosition = snapPointToGridCorners(mousePosition, this.thickness, this.thickness); //Position am nächsten Rasterpunkt
        if (gridPosition.distanceTo(mousePosition) <= gridSnapTolerance) {
            return gridPosition;
        }

        return null; //Wenn weder noch zutrifft null zurückgeben. Kein Snappoint konnte berechnet werden. Wird anschließend an Mausposition gesetzt
    }

    snapPointToAxis(point) {

        removeVisualAxis();
        let closestPoint = null;
        if (wallSnapAxis.z.length > 0) {
            closestPoint = this.snapPointToSingleAxis(point, wallSnapAxis.z, "x", this.thickness * 0.5);
        }
        if (wallSnapAxis.x.length > 0) {
            let tmpClosestPoint = null;
            if (closestPoint !== null) {
                tmpClosestPoint = this.snapPointToSingleAxis(closestPoint, wallSnapAxis.x, "z", this.thickness * 0.5);
            } else {
                tmpClosestPoint = this.snapPointToSingleAxis(point, wallSnapAxis.x, "z", this.thickness * 0.5);
            }

            if (tmpClosestPoint !== null) {
                closestPoint = tmpClosestPoint;
            }
        }

        return closestPoint;
    }

    snapPointToSingleAxis(point, lines, direction, offset = HBEWallDefaultThickness * 0.5) {
        if (point === null || point === undefined) {
            return null;
        }

        const nearest = nearestAxisCoordinate(point, lines, direction, SNAP_AXIS_TOLERANCE);
        if (nearest === null) {
            return null;
        }

        // The beam starts flush with the outer face of the post or wall and the body runs
        // toward the cursor, across that support.
        const mate = this.supportMateCoord(nearest.coord, lines, direction);
        let face = nearest.coord;
        let toward = point[direction] >= nearest.coord ? 1 : -1;
        if (mate !== null) {
            const low = Math.min(nearest.coord, mate);
            const high = Math.max(nearest.coord, mate);
            toward = point[direction] >= (low + high) / 2 ? 1 : -1;
            face = toward > 0 ? low : high;
        }

        const closestPoint = new Vector3().copy(point);
        const guide = new Vector3().copy(point);
        if (direction === "x") {
            guide.x = face;
            addVisualAxis(guide, wallDirectionAxis.xAxis);
            closestPoint.x = face + toward * offset;
        } else {
            guide.z = face;
            addVisualAxis(guide, wallDirectionAxis.zAxis);
            closestPoint.z = face + toward * offset;
        }
        closestPoint.y = previewYPosition;
        return closestPoint;
    }

    // Posts are as wide as the beam on them, walls are 10 cm.
    supportSize() {
        return Math.max(this.thickness, HBEWallDefaultThickness);
    }

    // The other face of the post or wall: 10 cm for a wall, up to the beam width for a post.
    supportMateCoord(coord, lines, direction) {
        let mate = null;
        let best = Infinity;
        for (let i = 0; i < lines.length; i++) {
            const other = lines[i].start[direction];
            const gap = Math.abs(other - coord);
            if (gap < HBEWallDefaultThickness * 0.4 || gap > this.supportSize() + 0.03) {
                continue;
            }
            const score = Math.min(Math.abs(gap - HBEWallDefaultThickness), Math.abs(gap - this.thickness));
            if (score < best) {
                best = score;
                mate = other;
            }
        }
        return mate;
    }

    checkValidFirstPosition() {
        // A beam may start in empty space. It only has to stay off doors, windows and other openings.
        this.validFirstPosition = !this.checkForControlObjectIntersection(this.firstPreviewMesh);

        if (this.validFirstPosition) {
            this.firstPreviewMesh.material = previewMaterial;
        } else {
            this.firstPreviewMesh.material = previewNotAllowedMaterial;
        }
    }

    createSecondPreview() {
        //Aufruf aus Klick Event.
        //Entfernt die erste preview aus der Ansicht. Wird aber für spätere Berechnungen noch beibehalten.
        this.firstPreviewMesh.removeFromParent();
        this.adjustSecondPreview();
    }

    adjustSecondPreview(overwriteLength = null) {
        //Relevante Werte vor Berechnung zurücksetzen
        this.direction = null;
        this.directionAxis = null;
        this.directionVector = null;
        this.firstPositionOverwrite = null;
        this.cancelAfterBeamCreation = false;

        //Prüfen ob Richtung begrenzt werden muss: Abhängig von vorher platzierten Wänden, während der gleichen Vorschau
        let allowedDirections = [];
        if (this.previousDirection !== null) {
            //Erlaubte Richtungen festlegen
            if (this.previousDirection === wallDirection.negativeX || this.previousDirection === wallDirection.positiveX) {
                allowedDirections = [wallDirection.negativeZ, wallDirection.positiveZ];
            } else if (this.previousDirection === wallDirection.negativeZ || this.previousDirection === wallDirection.positiveZ) {
                allowedDirections = [wallDirection.negativeX, wallDirection.positiveX];
            }
        }

        //Orientierung und Länge der Wand/Wandvorschau berechnen. Basierend auf Position der ersten Vorschau und der aktuellen Mausposition. Sowie Richtungsbegrenzungen
        removeVisualAxis();
        this.calculateDirectionVector(allowedDirections);

        //Wenn zweite Wand während einer Vorschau generiert wird:
        this.adjustFirstPreviewPosition();
        if (isOrthogonalMode && this.directionAxis) {
            this.applyDirectionOffset();
        }
        this.alignToParallelShell();
        this.seatStartFlush();

        const rawLength = this.directionVector.length();
        const rawEnd = new Vector3().copy(this.firstPositionOverwrite).add(this.directionVector);
        let closure = null;
        if (overwriteLength === null && rawLength > 0) {
            closure = this.snapEndToClosure(rawEnd);
        }

        let endGuide = null;
        if (closure !== null) {
            closure = {point: this.clampEndToSupport(closure.point), butt: closure.butt};
            this.directionVector = new Vector3().subVectors(closure.point, this.firstPositionOverwrite);
            this.length = this.directionVector.length();
            this.cancelAfterBeamCreation = closure.butt;
            endGuide = closure.point;
        } else {
            if (isFreeWallMode) {
                this.length = toFixedFloat(rawLength, 5);
            } else if (overwriteLength !== null) {
                this.length = toFixedFloat(overwriteLength, 5);
            } else {
                let sections = Math.round((toFixedFloat(rawLength, 5)) / Globals.HBESectionWidth);
                this.length = sections * Globals.HBESectionWidth;
            }
            this.directionVector.clampLength(this.length, this.length);
            this.cancelAfterBeamCreation = false;
        }

        if (this.length > 0) {
            const rayHit = this.snapToExistingWalls(rawLength, overwriteLength === null);
            const rayPoint = rayHit === null ? null : this.clampEndToSupport(this.extendToFarSupportFace(rayHit));
            if (rayPoint !== null) {
                const rayVector = new Vector3().subVectors(rayPoint, this.firstPositionOverwrite);
                if (closure === null || rayVector.length() < this.length - (Math.max(this.thickness, HBEWallDefaultThickness) + 0.02)) {
                    this.directionVector = rayVector;
                    this.length = rayVector.length();
                    this.cancelAfterBeamCreation = false;
                    endGuide = rayPoint;
                }
            }
        }
        if (endGuide !== null && this.directionAxis) {
            addVisualAxis(endGuide, this.directionAxis);
        }
        // Ending on an existing wall, beam or post finishes the run, like closing the loop does.
        if (!this.cancelAfterBeamCreation && this.directionAxis && this.length > 0) {
            const axis = directionAxisName(this.direction);
            const lateral = axis === "x" ? "z" : "x";
            const end = this.firstPositionOverwrite[axis] + this.directionVector[axis];
            this.cancelAfterBeamCreation = landsOnMember(end, this.firstPositionOverwrite[lateral], axis, axis === "x" ? wallSnapAxis.z : wallSnapAxis.x, ["wall", "beam", "pillar"]);
        }

        //Generierung des eigentlichen Vorschauelements mit berechneten Werten
        this.adjustSecondPreviewMesh();
    }

    calculateDirectionVector(allowedDirections = []) {
        //Richtungsvektor berechnen
        let directionVector = new Vector3().subVectors(mousePosition, this.firstPreviewMesh.position);
        this.firstPositionOverwrite = new Vector3().copy(this.firstPreviewMesh.position); //Position der ersten Vorschau. Punkt wird anschließend abhängig von der Wandrichtung verschoben
        directionVector.setY(0); //Y Koordinate für Berechnung irrelevant > Nullen > Verfälscht sonst die Länge der Wand

        //Zunächst nur orthogonale Wände. Muss ggf. später erweitert werden
        if (isOrthogonalMode) {
            //Ausrichtung entweder nach Richtung mit größerer Distanz oder wenn Richtung vorgegeben per Array dann in diese begrenzen
            let xOriented = Math.abs(directionVector.x) > Math.abs(directionVector.z);
            let directionZAllowed = allowedDirections.includes(wallDirection.positiveZ) || allowedDirections.includes(wallDirection.negativeZ);
            let directionXAllowed = allowedDirections.includes(wallDirection.positiveX) || allowedDirections.includes(wallDirection.negativeX);

            if ((xOriented && !directionZAllowed) || directionXAllowed) {
                let offsetVector = new Vector3(beamVectorOffset, 0, 0);
                if (mousePosition.x > this.firstPreviewMesh.position.x) {
                    this.direction = wallDirection.positiveX;
                    this.firstPositionOverwrite.sub(offsetVector);
                } else {
                    this.direction = wallDirection.negativeX;
                    this.firstPositionOverwrite.add(offsetVector);
                }
                this.directionAxis = wallDirectionAxis.xAxis;
                directionVector.setZ(0);
            } else {
                let offsetVector = new Vector3(0, 0, beamVectorOffset);
                if (mousePosition.z > this.firstPreviewMesh.position.z) {
                    this.direction = wallDirection.positiveZ;
                    this.firstPositionOverwrite.sub(offsetVector);
                } else {
                    this.direction = wallDirection.negativeZ;
                    this.firstPositionOverwrite.add(offsetVector);
                }
                this.directionAxis = wallDirectionAxis.zAxis;
                directionVector.setX(0);
            }
        }

        let currentLength = directionVector.length(); //Länge des Vektors
        directionVector.clampLength(currentLength + beamVectorOffset, currentLength + beamVectorOffset); //Verlängern um die Distanz zwischen erstem Viereck Mittelpunkt bis Bounds

        this.directionVector = directionVector;
    }

    applyDirectionOffset() {
        const start = this.firstPreviewMesh.position;
        this.firstPositionOverwrite = new Vector3().copy(start);
        const directionVector = new Vector3().subVectors(mousePosition, start);
        directionVector.setY(0);

        if (this.directionAxis === wallDirectionAxis.xAxis) {
            const offsetVector = new Vector3(beamVectorOffset, 0, 0);
            if (this.direction === wallDirection.positiveX) {
                this.firstPositionOverwrite.sub(offsetVector);
            } else {
                this.firstPositionOverwrite.add(offsetVector);
            }
            directionVector.setZ(0);
        } else {
            const offsetVector = new Vector3(0, 0, beamVectorOffset);
            if (this.direction === wallDirection.positiveZ) {
                this.firstPositionOverwrite.sub(offsetVector);
            } else {
                this.firstPositionOverwrite.add(offsetVector);
            }
            directionVector.setX(0);
        }

        const currentLength = directionVector.length();
        directionVector.clampLength(currentLength + beamVectorOffset, currentLength + beamVectorOffset);
        this.directionVector = directionVector;
    }

    // Beams only turn inner: the previous beam keeps the corner post and the new beam starts on it.
    // The corner post is as wide as the beam.
    adjustFirstPreviewPosition() {
        if (!this.firstBeamPlaced || !this.previousEndPoint || !this.previousDirection) {
            return;
        }
        if (!this.directionAxis) {
            this.firstPreviewMesh.position.copy(this.previousEndPoint);
            return;
        }

        const prev = {end: this.previousEndPoint, axis: directionAxisName(this.previousDirection), sign: directionSign(this.previousDirection)};
        const corner = chooseStartCorner(this.chain, prev, directionAxisName(this.direction), directionSign(this.direction), mousePosition, this.thickness, beamCornerStyles);
        this.firstPreviewMesh.position.x = corner.center.x;
        this.firstPreviewMesh.position.z = corner.center.z;
        addVisualAxis(this.firstPreviewMesh.position, prev.axis === "x" ? wallDirectionAxis.xAxis : wallDirectionAxis.zAxis);
    }

    // The solid starts on the outer face of the post or wall it leaves, so its first part sits on that support.
    seatStartFlush() {
        if (!this.directionAxis || !this.firstPositionOverwrite || !this.directionVector) {
            return;
        }

        const axis = this.directionAxis === wallDirectionAxis.xAxis ? "x" : "z";
        const other = axis === "x" ? "z" : "x";
        const forward = Math.sign(this.directionVector[axis]);
        if (forward === 0) {
            return;
        }

        const lines = axis === "x" ? wallSnapAxis.z : wallSnapAxis.x;
        const origin = this.firstPositionOverwrite[axis];
        const lateral = this.firstPositionOverwrite[other];
        const support = this.supportSize();
        let far = null;
        let supportFar = null;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const coord = line.start[axis];
            const along = (coord - origin) * forward;
            if (along > support + 0.03 || along < -(support + 0.08)) {
                continue;
            }
            if (this.faceGap(lateral, line.start[other], line.end[other]) > support + 0.2) {
                continue;
            }
            if (Math.abs(line.start[other] - line.end[other]) < 0.08) {
                continue;
            }
            if (far === null || along < far.along - 0.0005) {
                far = {coord, along};
            }
            if ((line.snapKind === "pillar" || line.snapKind === "wall") && (supportFar === null || along < supportFar.along - 0.0005)) {
                supportFar = {coord, along};
            }
        }
        far = supportFar || far;
        if (far === null) {
            return;
        }

        const shift = far.coord - origin;
        if (Math.abs(shift) < 0.0005) {
            return;
        }
        if ((this.directionVector[axis] - shift) * forward < SNAP_MIN_SEGMENT) {
            return;
        }

        this.firstPositionOverwrite[axis] = far.coord;
        this.directionVector[axis] -= shift;
    }

    // A ray stops on the near face. The beam has to reach the outer face of that support.
    extendToFarSupportFace(point) {
        if (!point || !this.directionAxis || !this.directionVector) {
            return point;
        }

        const axis = this.directionAxis === wallDirectionAxis.xAxis ? "x" : "z";
        const other = axis === "x" ? "z" : "x";
        const forward = Math.sign(this.directionVector[axis]);
        if (forward === 0) {
            return point;
        }

        const lines = axis === "x" ? wallSnapAxis.z : wallSnapAxis.x;
        const origin = point[axis];
        const lateral = this.firstPositionOverwrite ? this.firstPositionOverwrite[other] : point[other];
        const support = this.supportSize();
        let far = origin;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const coord = line.start[axis];
            const along = (coord - origin) * forward;
            if (along <= 0.02 || along > support + 0.03) {
                continue;
            }
            if (this.faceGap(lateral, line.start[other], line.end[other]) > support + 0.2) {
                continue;
            }
            if (Math.abs(line.start[other] - line.end[other]) < 0.08) {
                continue;
            }
            if ((coord - far) * forward > 0) {
                far = coord;
            }
        }
        if (far === origin) {
            return point;
        }

        const seated = point.clone();
        seated[axis] = far;
        return seated;
    }

    // The end stops on the outer face of the post or wall, not past it.
    clampEndToSupport(point) {
        if (!point || !this.directionAxis || !this.directionVector || !this.firstPositionOverwrite) {
            return point;
        }

        const axis = this.directionAxis === wallDirectionAxis.xAxis ? "x" : "z";
        const other = axis === "x" ? "z" : "x";
        const forward = Math.sign(this.directionVector[axis]);
        if (forward === 0) {
            return point;
        }

        const lines = axis === "x" ? wallSnapAxis.z : wallSnapAxis.x;
        const origin = point[axis];
        const lateral = this.firstPositionOverwrite[other];
        const support = this.supportSize();
        let face = null;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.snapKind !== "pillar" && line.snapKind !== "wall") {
                continue;
            }
            const coord = line.start[axis];
            const along = (coord - origin) * forward;
            if (along < -0.05 || along > support + 0.04) {
                continue;
            }
            if (this.faceGap(lateral, line.start[other], line.end[other]) > support + 0.2) {
                continue;
            }
            if (Math.abs(line.start[other] - line.end[other]) < 0.08) {
                continue;
            }
            if (face === null || along > face.along) {
                face = {coord, along};
            }
        }
        if (face === null) {
            return point;
        }

        const seated = point.clone();
        seated[axis] = face.coord;
        return seated;
    }

    faceGap(value, a, b) {
        const min = Math.min(a, b);
        const max = Math.max(a, b);
        if (value >= min && value <= max) {
            return 0;
        }
        return Math.min(Math.abs(value - min), Math.abs(value - max));
    }

    alignToParallelShell() {
        if (this.firstBeamPlaced || !this.directionAxis || !this.firstPreviewMesh) {
            return;
        }

        const lateral = this.directionAxis === wallDirectionAxis.xAxis ? "z" : "x";
        const lines = lateral === "z" ? wallSnapAxis.x : wallSnapAxis.z;
        const center = parallelCenterline(this.firstPreviewMesh.position[lateral], lines, lateral, SNAP_ALIGN_LATERAL);
        if (center === null) {
            return;
        }

        this.firstPreviewMesh.position[lateral] = center;
        this.applyDirectionOffset();
        const axis = lateral === "z" ? wallDirectionAxis.zAxis : wallDirectionAxis.xAxis;
        addVisualAxis(this.firstPreviewMesh.position, axis);
    }

    // One end snap: closing the loop wins, then the nearest face or return-to-start target.
    // A face snap only lines the end up; the run stops only when the loop closes.
    snapEndToClosure(rawEnd) {
        if (!this.directionAxis || !this.firstPositionOverwrite) {
            return null;
        }

        const axis = directionAxisName(this.direction);
        const sign = directionSign(this.direction);
        const lines = axis === "x" ? wallSnapAxis.z : wallSnapAxis.x;
        const tolerance = this.joinSnapTolerance();
        const face = snapEndToFace(this.firstPositionOverwrite, rawEnd, axis, lines, {
            prefer: "far",
            minWallFace: 0.08,
            lateralTolerance: 100,
            tolerance
        });
        const end = pickChainEnd(this.chain, this.firstPositionOverwrite, rawEnd, axis, sign, this.thickness, {
            tolerance,
            minSegment: SNAP_MIN_SEGMENT,
            styles: beamCornerStyles,
            faces: face ? [face[axis]] : []
        });
        if (end === null) {
            return null;
        }

        const point = rawEnd.clone();
        point[axis] = end.coord;
        point[axis === "x" ? "z" : "x"] = this.firstPositionOverwrite[axis === "x" ? "z" : "x"];
        return {point, butt: end.kind !== null};
    }

    joinSnapTolerance() {
        return Math.max(SNAP_CLOSE_TOLERANCE, Globals.HBESectionWidth, SNAP_JOIN_ALONG);
    }

    snapTargets() {
        return this.allRaehmMeshes.concat(this.beamMeshes, this.pillarMeshes);
    }

    snapToExistingWalls(rawLength = this.length, allowExtend = false) {
        const targets = this.snapTargets();
        const ySource = this.allRaehmMeshes.length > 0 ? this.allRaehmMeshes : (this.beamMeshes.length > 0 ? this.beamMeshes : this.pillarMeshes);
        let yCenter = 1;
        if (ySource.length > 0) {
            const bounds = setBox3FromObjectArray(ySource);
            if (!bounds.isEmpty()) {
                yCenter = bounds.getCenter(new Vector3()).y;
            }
        }
        let direction = new Vector3().copy(this.directionVector).clampLength(1, 1);  //Normalisierte Richtung der Wand für Raycaster
        let originMin, originMax;
        let firstPreviewBounds = new Box3().setFromObject(this.firstPreviewMesh);

        //Linke und Rechte Bounds der Wand in Laufrichtung
        if (this.directionAxis === wallDirectionAxis.xAxis) {
            originMin = new Vector3(this.firstPreviewMesh.position.x, yCenter, firstPreviewBounds.min.z + wallRaycastTolerance);
            originMax = new Vector3(this.firstPreviewMesh.position.x, yCenter, firstPreviewBounds.max.z - wallRaycastTolerance);
        } else {
            originMin = new Vector3(firstPreviewBounds.min.x + wallRaycastTolerance, yCenter, this.firstPreviewMesh.position.z);
            originMax = new Vector3(firstPreviewBounds.max.x - wallRaycastTolerance, yCenter, this.firstPreviewMesh.position.z);
        }

        //Intersection mit Rähmen bestimmen für beide Boundpunkte
        raehmRaycaster.set(originMin, direction);
        let intersectionsMin = raehmRaycaster.intersectObjects(targets, false);
        raehmRaycaster.set(originMax, direction);
        let intersectionsMax = raehmRaycaster.intersectObjects(targets, false);

        //Wenn intersection gefunden: Prüfen ob die Länge bis zum Punkt kürzer wäre als die aktuelle Wandlänge > Relevanter Snappoint. Sonst null zurückgeben
        let minSnapPoint = null;
        let minSnapDistanceTo = null;
        if (intersectionsMin.length > 0) {
            let snapPoint = new Vector3(intersectionsMin[0].point.x, originMin.y, intersectionsMin[0].point.z);
            let distance = originMin.distanceTo(snapPoint);
            if (this.acceptsSnapDistance(distance, rawLength, allowExtend)) {
                minSnapPoint = snapPoint;
                minSnapDistanceTo = distance;
            }
        }

        let maxSnapPoint = null;
        let maxSnapDistanceTo = null;
        if (intersectionsMax.length > 0) {
            let snapPoint = new Vector3(intersectionsMax[0].point.x, originMax.y, intersectionsMax[0].point.z);
            let distance = originMax.distanceTo(snapPoint);
            if (this.acceptsSnapDistance(distance, rawLength, allowExtend)) {
                maxSnapPoint = snapPoint;
                maxSnapDistanceTo = distance;
            }
        }

        let snapPoint = null;
        //Snappoints prüfen. Wenn beide gefunden > Nächsten nehmen. Wenn nur einer diesen. Sonst keinen Snappunkt zurückgeben
        if (maxSnapPoint !== null && minSnapPoint !== null) {
            snapPoint = minSnapDistanceTo < maxSnapDistanceTo ? minSnapPoint : maxSnapPoint;
        } else if (maxSnapPoint !== null) {
            snapPoint = maxSnapPoint;
        } else if (minSnapPoint !== null) {
            snapPoint = minSnapPoint;
        }

        if (snapPoint !== null) {
            snapPoint.y = previewYPosition;
            if (this.directionAxis === wallDirectionAxis.xAxis) {
                snapPoint.z = this.firstPositionOverwrite.z;
            } else {
                snapPoint.x = this.firstPositionOverwrite.x;
            }
            return snapPoint;
        }

        return null;
    }

    acceptsSnapDistance(distance, rawLength, allowExtend) {
        if (distance < SNAP_MIN_HIT) {
            return false;
        }
        if (distance <= this.length + 0.001) {
            return true;
        }
        return allowExtend && Math.abs(distance - rawLength) <= SNAP_CLOSE_TOLERANCE;
    }

    adjustSecondPreviewMesh() {
        //Position des zweiten Vorschau Meshes
        let position = new Vector3().copy(this.firstPositionOverwrite).add(new Vector3().copy(this.directionVector).multiplyScalar(0.5));
        position.y = previewYPosition;

        //Neuen Mesh generieren und der Szene hinzufügen, falls erste mal aufgerufen. Sonst nur Geometrie aktualisieren > Performancegründe
        if (this.secondPreviewMesh === null) {
            this.secondPreviewMesh = this.createSecondPreviewMesh();
            this.measurementText = this.createMeasurementText();
            setObjectLayer(this.measurementText, Globals.layers.layoutViewOnly);
            tmpGroup.addWithUuid(this.secondPreviewMesh);
            tmpGroup.addWithUuid(this.measurementText);
        } else {
            if (this.newBeamPlaced) {
                tmpGroup.addWithUuid(this.secondPreviewMesh);
                tmpGroup.addWithUuid(this.measurementText);
                this.newBeamPlaced = false;
            }
            let geometry = new PlaneGeometry(this.length, this.thickness);
            geometry.rotateX(Math.PI / 2); //Rotieren um auf x/z zu liegen
            this.secondPreviewMesh.geometry.dispose();
            this.secondPreviewMesh.geometry = geometry;

            this.measurementText = this.createMeasurementText(this.measurementText);
        }

        //Rotation der Vorschau basierend auf Wandrichtung einstellen
        if (this.direction === wallDirection.positiveZ) {
            this.secondPreviewMesh.rotation.y = -Math.PI / 2;
        } else if (this.direction === wallDirection.negativeZ) {
            this.secondPreviewMesh.rotation.y = Math.PI / 2;
        } else {
            this.secondPreviewMesh.rotation.y = 0;
        }

        //Position übernehmen
        this.secondPreviewMesh.position.copy(position);

        this.checkValidSecondPosition();
    }

    checkValidSecondPosition() {
        this.validSecondPosition = !this.checkForControlObjectIntersection(this.secondPreviewMesh);

        if (this.validSecondPosition) {
            this.secondPreviewMesh.material = previewMaterial;
        } else {
            this.secondPreviewMesh.material = previewNotAllowedMaterial;
        }
    }

    checkForControlObjectIntersection(previewMesh) {
        //Checken ob die preview ein ControlObject schneidet
        let previewBounds = new Box3().setFromObject(previewMesh);
        previewBounds.min.y = 0;
        previewBounds.max.y = 1;

        for (let i = 0; i < this.controlObjects.length; i++) {
            let controlObjectsBounds = new Box3().setFromObject(this.controlObjects[i]);
            controlObjectsBounds.min.y = 0;
            controlObjectsBounds.max.y = 1;

            if (!(controlObjectsBounds.intersect(previewBounds).isEmpty())) {
                return true;
            }
        }

        return false;
    }

    createSecondPreviewMesh() {
        //Generiere Vorschau für ersten Wandklick
        let geometry = new PlaneGeometry(this.length, this.thickness);
        geometry.rotateX(Math.PI / 2); //Rotieren um auf x/z zu liegen
        let previewMesh = new Mesh(geometry, previewMaterial);
        previewMesh.name = "beamPreview";
        previewMesh.material = previewMaterial;
        setObjectLayer(previewMesh, Globals.layers.layoutViewOnly);
        return previewMesh;
    }

    //Functions for creating the beam
    createBeamFromPreview() {
        if (!this.firstBeamPlaced && this.directionAxis) {
            // Taken from the seated start face, not the start marker, so closing targets match the real beam.
            const axis = directionAxisName(this.direction);
            const sign = directionSign(this.direction);
            const start = {x: this.firstPositionOverwrite.x, z: this.firstPositionOverwrite.z};
            start[axis] += sign * this.thickness * 0.5;
            this.chain = {start, axis, sign};
        }

        //Erstellung der eigentlich Wand basierend auf der Vorschau
        TransactionManager.startTransaction();

        this.secondPreviewMesh.removeFromParent();
        this.measurementText.removeFromParent();

        let supportBeamObject = new SupportBeam(this);
        supportBeamObject.showWarnings = !this.firstBeamPlaced;
        let supportBeam = supportBeamObject.create();

        //TODO: Wand Richtung / Start&Endpunkt mit in userData aufnehmen?
        buildingGroup.addWithUuid(supportBeam); //Hinzufügen

        TransactionManager.stopTransaction();

        // Stop when this beam butts into a side, or when it is the piece that closes the shell.
        if (this.cancelAfterBeamCreation || (!this.shellWasClosed && isOuterShellClosed())) {
            clearBeamPreviewState();
            return false;
        }

        this.allRaehmMeshes = findChildrenByName(buildingGroup, raehmMeshName, false, true);
        this.beamMeshes = findChildrenByName(buildingGroup, supportBeamMeshName, false, true);
        this.pillarMeshes = findChildrenByName(buildingGroup, pillarMeshName, false, true);

        this.firstBeamPlaced = true;
        this.newBeamPlaced = true;

        //Position der ersten Vorschau updaten an das Ende der angelegten Wand. Für Berechnung der nächsten Wandvorschau
        let tmpLength = this.directionVector.length();
        let tmpDirectionVector = new Vector3().copy(this.directionVector).clampLength(tmpLength - beamVectorOffset, tmpLength - beamVectorOffset);
        let newPreviewPosition = new Vector3().copy(this.firstPositionOverwrite).add(tmpDirectionVector);
        this.firstPreviewMesh.position.copy(newPreviewPosition);
        this.previousDirection = this.direction;
        this.previousEndPoint = new Vector3().copy(this.firstPositionOverwrite).add(this.directionVector);

        return true;
    }

    //Utils
    createMeasurementText(measurementText = null) {
        let size = new Box3().setFromObject(this.secondPreviewMesh).getSize(new Vector3());
        let text = Math.abs(this.directionVector.x) > Math.abs(this.directionVector.z) ? toFixedFloat(size.x) : toFixedFloat(size.z);
        text = text.toFixed(Globals.roundToDecimals);

        if (measurementText === null) {
            measurementText = new Text(text, 0.2, 'center', -0.25, new Vector3(0, 0, 0), new Euler(-Math.PI / 2, 0, 0));
            measurementText.color = 0x000000;
        } else {
            measurementText.text = text;
        }

        measurementText.sync(() => {
            //Position measurement text
            measurementText.position.x = this.secondPreviewMesh.position.x;
            measurementText.position.y = previewYPosition;
            measurementText.position.z = this.secondPreviewMesh.position.z;
            measurementText.rotation.z = this.secondPreviewMesh.rotation.y === 0 ? 0 : Math.PI / 2;
            render();
        });

        return measurementText;
    }
}
